import { useEffect, useState } from "react";
import type { CapabilityReport } from "../media/capabilities";
import { inspectCapabilities } from "../media/capabilities";
import type { DiagnosticsRecord, EditingProject } from "../types/project";
import { APP_VERSION } from "../types/project";

type Props = {
  project?: EditingProject;
  record: DiagnosticsRecord;
  serviceWorkerVersion: string | null;
  onClose: () => void;
};

export function Diagnostics({
  project,
  record,
  serviceWorkerVersion,
  onClose,
}: Props) {
  const [capabilities, setCapabilities] = useState<CapabilityReport | null>(
    null,
  );
  const [storage, setStorage] = useState<StorageEstimate | null>(null);

  useEffect(() => {
    void inspectCapabilities(project?.sampleRate, project?.channels).then(
      setCapabilities,
    );
    void navigator.storage?.estimate().then(setStorage);
  }, [project]);

  const rows: Array<[string, string]> = [
    ["App version", `v${APP_VERSION}`],
    ["Service Worker build", serviceWorkerVersion ?? "開発環境 / 未取得"],
    ["Browser / OS", navigator.userAgent],
    ["WebCodecs", yesNo(capabilities?.webCodecs)],
    ["AudioDecoder", yesNo(capabilities?.audioDecoder)],
    ["AudioEncoder", yesNo(capabilities?.audioEncoder)],
    ["H.264 1280×720", yesNo(capabilities?.h264)],
    [
      "AAC 約192kbps",
      capabilities
        ? `${yesNo(capabilities.aac)} (${capabilities.aacMode})`
        : "確認中",
    ],
    ["OPFS", yesNo(capabilities?.opfs)],
    ["Web Share", yesNo(capabilities?.webShare)],
    ["Screen Wake Lock", yesNo(capabilities?.wakeLock)],
    [
      "Storage",
      storage
        ? `${formatBytes(storage.usage ?? 0)} / ${formatBytes(storage.quota ?? 0)}`
        : "確認中",
    ],
    ["入力codec", project?.codec ?? record.inputCodec ?? "—"],
    ["Sample rate", project ? `${project.sampleRate} Hz` : "—"],
    ["Channels", project ? String(project.channels) : "—"],
    [
      "入力duration",
      project
        ? `${(project.durationFrames / project.sampleRate).toFixed(3)} s`
        : "—",
    ],
    [
      "全体波形生成",
      record.overviewSeconds !== undefined
        ? `${record.overviewSeconds.toFixed(3)} s`
        : "—",
    ],
    [
      "詳細波形生成",
      record.detailSeconds !== undefined
        ? `${record.detailSeconds.toFixed(3)} s`
        : "—",
    ],
    ["出力codec", record.outputCodec ?? "—"],
    [
      "出力duration",
      record.outputDurationSeconds !== undefined
        ? `${record.outputDurationSeconds.toFixed(3)} s`
        : "—",
    ],
    [
      "動画生成",
      record.exportSeconds !== undefined
        ? `${record.exportSeconds.toFixed(3)} s`
        : "—",
    ],
    [
      "出力サイズ",
      record.outputBytes !== undefined ? formatBytes(record.outputBytes) : "—",
    ],
    ["最後のエラー", record.lastError ?? "なし"],
  ];

  return (
    <div
      className="modal diagnostics"
      role="dialog"
      aria-modal="true"
      aria-labelledby="diagnostics-title"
    >
      <header className="modal__header">
        <button type="button" className="text-button" onClick={onClose}>
          閉じる
        </button>
        <h2 id="diagnostics-title">Diagnostics</h2>
        <span className="modal__header-spacer" />
      </header>
      <div className="diagnostics__body">
        <p>実機検証用の端末内情報です。音声内容や個人情報は送信されません。</p>
        <dl>
          {rows.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

function yesNo(value: boolean | undefined): string {
  return value === undefined ? "確認中" : value ? "利用可能" : "未対応";
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KiB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
}
